// EducationDialog.jsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Stack,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import axiosInstance from "../../../axios/axiosInstance";
import { degreeOptions } from "../../corporate/Constants";

export default function EducationDialog({
  open,
  onClose,
  onSave,
  formData,
  setFormData,
  editingId,
  educationList,
}) {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: formData,
  });

  const declarationAccepted = watch("declaration");

  // --- State for Degrees ---
  // const [degQuery, setDegQuery] = useState("");
  // const [degOptions, setDegOptions] = useState([]);
  // const [degLoading, setDegLoading] = useState(false);

  // ---------------------------
  // 🎓 Fetch Degrees/Programs
  // ---------------------------
  // useEffect(() => {
  //   if (!degQuery || degQuery.trim().length < 2) {
  //     setDegOptions([]);
  //     return;
  //   }

  //   const timeoutId = setTimeout(async () => {
  //     try {
  //       setDegLoading(true);
  //       const { data } = await axiosInstance.get(
  //         `/api/profile/programs?name=${encodeURIComponent(degQuery)}`
  //       );

  //       if (Array.isArray(data) && data.length > 0) {
  //         setDegOptions(data.map((item) => item.name || item));
  //       } else {
  //         setDegOptions([degQuery]); // fallback
  //       }
  //     } catch (err) {
  //       console.error("❌ Error fetching programs:", err);
  //       setDegOptions([degQuery]);
  //     } finally {
  //       setDegLoading(false);
  //     }
  //   }, 400);

  //   return () => clearTimeout(timeoutId);
  // }, [degQuery]);

  // Reset form when editing or adding new
  useEffect(() => {
    if (formData) reset(formData);
  }, [formData, reset]);

  const handleFormSubmit = (data) => {
    // Check for duplicates
    if (educationList?.length > 0) {
      const isDuplicate = educationList.some(
        (edu) =>
          edu.id !== editingId &&
          edu.degree === data.degree &&
          edu.fieldOfStudy === data.fieldOfStudy,
      );

      if (isDuplicate) {
        setError("degree", {
          type: "manual",
          message: "This education entry already exists.",
        });
        return;
      }
    }

    // Transform declaration to terms_accepted for backend
    const { declaration, ...restData } = data;
    const transformedData = {
      ...restData,
      terms_accepted: declaration ? 1 : 0,
    };
    onSave(transformedData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "#6c5ce7",
          color: "white",
        }}
      >
        {editingId ? "Edit Education" : "Add Education"}
      </DialogTitle>

      <DialogContent
        sx={{
          overflowY: "auto",
          maxHeight: "80vh",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 8,
          },
        }}
      >
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* 🎓 Degree/Program */}
            <Controller
              name="degree"
              control={control}
              rules={{ required: "Degree or program is required" }}
              render={({ field }) => (
                <Autocomplete
                  options={degreeOptions}
                  value={
                    degreeOptions.find(
                      (option) => option.value === field.value,
                    ) || null
                  }
                  onChange={(e, newValue) => {
                    field.onChange(newValue ? newValue.value : "");
                  }}
                  getOptionLabel={(option) => option.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Degree/Program *"
                      fullWidth
                      error={!!errors.degree}
                      helperText={errors.degree?.message}
                    />
                  )}
                />
              )}
            />

            {/* Field of Study */}
            <Controller
              name="fieldOfStudy"
              control={control}
              rules={{ required: "Field of study is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Field of Study *"
                  fullWidth
                  error={!!errors.fieldOfStudy}
                  helperText={errors.fieldOfStudy?.message}
                />
              )}
            />

            {/* Location */}
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Location (Optional)" fullWidth />
              )}
            />

            {/* Declaration Checkbox */}
            <Controller
              name="declaration"
              control={control}
              rules={{
                required: "You must accept the declaration to proceed",
              }}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I hereby declare that the information I have submitted is
                      true, correct, and complete. I acknowledge that EQuAlly
                      reserves the right to verify my qualifications and request
                      official documents and/or certificates at any stage of the
                      recruitment process.
                    </Typography>
                  }
                />
              )}
            />
            {errors.declaration && (
              <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                {errors.declaration.message}
              </Typography>
            )}
          </Stack>

          <DialogActions sx={{ mt: 2 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!declarationAccepted}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}
